import { createSelector, createSlice } from "@reduxjs/toolkit";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, queryMapFactory } from "../common/reducers/utilities";

const taskListEntrySlice = createSlice({
    name: 'taskListEntries',
    initialState: {
        statuses: [],
        data: {},
        entriesByList: {}
    },
    reducers: {
        setStatuses: (state, action) => {
            state.statuses = action.payload;
        },
        set: (state, action) => {
            ensureArray(action.payload).forEach(entry => {
                delete state.entriesByList[entry.listid];
            });
            Object.entries(groupBy(ensureArray(action.payload), 'listid')).forEach(([ listid, entries ]) => {
                entries.forEach(e => state.data[e.id] = e);
                state.entriesByList[listid] = entries;
            });
        },
        addOrUpdate: (state, action) => {
            for (const entry of ensureArray(action.payload)) {
                state.data[entry.id] = entry;
                if (!state.entriesByList[entry.listid]) {
                    state.entriesByList[entry.listid] = [entry];
                } else {
                    const idx = state.entriesByList[entry.listid]?.findIndex(e => entry.id === e.id);
                    if (idx >= 0) {
                        state.entriesByList[entry.listid].splice(idx, 1, entry);
                    } else {
                        state.entriesByList[entry.listid].push(entry);
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const entry of ensureArray(action.payload)) {
                delete state.data[entry.id]
                if (state.entriesByList[entry.listid]) {
                    state.entriesByList[entry.listid] = state.entriesByList[entry.listid].filter(e => e.id === entry.id);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => taskListEntrySlice.getInitialState());
    }
});

// #region network operations
const fetchTaskStatuses = network => network.get('/api/v1/taskStatuses', taskListEntrySlice.actions.setStatuses);
const fetchTaskListEntries = listid => network => network.get('/api/v1/taskListEntries/' + encodeURIComponent(listid), taskListEntrySlice.actions.set);
const fetchTaskListEntry = id => network => network.get('/api/v1/taskListEntry/' + encodeURIComponent(id), taskListEntrySlice.actions.set);
export const insertTaskListEntry = (network, entry) => network.post('/api/v1/taskListEntry', entry, taskListEntrySlice.actions.addOrUpdate);
export const updateTaskListEntry = (network, delta) => network.put('/api/v1/taskListEntry', delta, taskListEntrySlice.actions.addOrUpdate);
export const removeTaskListEntry = (network, id) => network.delete('/api/v1/taskListEntry/' + encodeURIComponent(id), taskListEntrySlice.actions.remove);
// #endregion

// #region selectors
const selectTaskStatuses = state => state.taskListEntries.statuses;
const selectTaskListEntriesMap = state => state.taskListEntries.entriesByList;
const selectTaskListEntryData = state => state.taskListEntries.data;
const selectAllListEntries = state => Object.values(state.taskListEntries.data);

const makeListSelector = createSelector([selectTaskListEntriesMap, (state, listid) => listid], (data, listid) => data[listid] ?? []);
const selectListEntries = listid => state => makeListSelector(state, listid);
const makeEntrySelector = createSelector([selectTaskListEntryData, (state, id) => id], (data, id) => data[id]);
const selectListEntry = id => state => makeEntrySelector(state, id);
// #endregion

// #region permission checks
export const canUpdateTaskListEntry = (credentials, taskList) => credentials.access.hasFull(['administrator', 'dispatcher'].concat(taskList.perms.filter(p => p.access == 1))) || taskList.write_users.includes(credentials.username) || (credentials.callsign && taskList.write_crews.includes(credentials.callsign));
export const canCreateTaskListEntry = (credentials, taskList) => credentials.access.hasFull(['administrator', 'dispatcher'].concat(taskList.perms.filter(p => p.access == 1))) || taskList.write_users.includes(credentials.username) || (credentials.callsign && taskList.write_crews.includes(credentials.callsign));
export const canRemoveTaskListEntry = (credentials, taskList) => credentials.access.hasFull(['administrator', 'dispatcher'].concat(taskList.perms.filter(p => p.access == 1))) || taskList.write_users.includes(credentials.username) || (credentials.callsign && taskList.write_crews.includes(credentials.callsign));
// #endregion

// #region listeners
const listen = {
    taskListEntryCreated: (entry, { dispatch }) => dispatch(taskListEntrySlice.actions.addOrUpdate(entry)),
    taskListEntryUpdated: (entry, { dispatch }) => dispatch(taskListEntrySlice.actions.addOrUpdate(entry)),
    taskListEntryRemoved: (entry, { dispatch }) => dispatch(taskListEntrySlice.actions.remove(entry))
}
// #endregion

const options = { cleanup: { root: taskListEntrySlice.name, all: selectAllListEntries, removeAll: taskListEntrySlice.actions.remove, remove: taskListEntrySlice.actions.remove } };
const initById = initMapFactory(fetchTaskListEntry, listen)
export const [useTaskListEntry] = dataHookFactory(selectListEntry, initById, options);
export const [useTaskListEntires] = dataHookFactory(selectListEntries, queryMapFactory(fetchTaskListEntries, 'id', initById, listen), options);
export const [useTaskStatuses] = dataHookFactory(selectTaskStatuses, { run: fetchTaskStatuses });

const reducerDetails = { name: taskListEntrySlice.name, reducer: taskListEntrySlice.reducer };
export default reducerDetails;